import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import LightboxModal from '../components/LightboxModal'
import { useCms } from '../context/CmsContext'
import { GALLERY_ITEMS } from '../data'

export default function HomePage() {
  const { siteSettings, getSection, getPage, tattooStyles, processSteps, galleries } = useCms()
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [isExpanded, setIsExpanded] = useState(false)

  // Get Forside data directly from pages collection
  const homePage = getPage('forside', {})

  // Sections from CMS page with fallback to getSection & defaults
  const heroSection = {
    title: homePage.heroTitle || getSection('home_hero').title || 'Skræddersyet Tatovering',
    eyebrow: homePage.heroEyebrow || getSection('home_hero').eyebrow || 'Docuit ink',
    subtitle: homePage.heroSubtitle || getSection('home_hero').subtitle || 'Eksklusivt tatoveringsstudie i Vester Sottrup nær Sønderborg',
    content: homePage.heroContent || getSection('home_hero').content || 'Hos Docuit ink på Gl. Landevej 14 i Vester Sottrup nær Sønderborg forvandler jeg dine idéer til unikt kropskunst. Jeg lægger vægt på kunstnerisk kvalitet, tryghed og tæt personlig dialog.',
    buttonText: homePage.heroButtonText || getSection('home_hero').buttonText || 'Send bookingforespørgsel',
    buttonUrl: homePage.heroButtonUrl || getSection('home_hero').buttonUrl || '/booking',
    secondaryButtonText: homePage.heroSecondaryButtonText || getSection('home_hero').secondaryButtonText || 'Ring nu',
    secondaryButtonUrl: homePage.heroSecondaryButtonUrl || getSection('home_hero').secondaryButtonUrl || `tel:${siteSettings.phoneRaw || '28907550'}`,
    imageUrl: homePage.heroImage || getSection('home_hero').imageUrl || siteSettings.heroImage
  }

  const introSection = {
    title: homePage.introTitle || getSection('home_intro').title || 'Fra din første tanke til færdigt kunstværk på huden',
    eyebrow: homePage.introEyebrow || getSection('home_intro').eyebrow || 'Om Docuit ink',
    subtitle: homePage.introSubtitle || getSection('home_intro').subtitle || 'Kunstnerisk håndværk og tæt dialog',
    content: homePage.introContent || getSection('home_intro').content || '<p>Hos Docuit ink er en tatovering ikke blot et motiv; det er et samarbejde. Jeg tager mig god tid til at lytte til dine tanker, gennemgå dine referencebilleder og tilpasse designet nøjagtigt til din krop og personlighed.</p><p>Jeg arbejder under de højeste hygiejnestandarder med kvalitetsudstyr og nåle for at sikre et rent, smukt og holdbart resultat. Uanset om det er et lille minimalistisk motiv eller et større custom sleeve, er din tryghed min vigtigste prioritet.</p>',
    buttonText: homePage.introButtonText || getSection('home_intro').buttonText || 'Book Tid',
    buttonUrl: '/booking',
    secondaryButtonText: homePage.introSecondaryButtonText || getSection('home_intro').secondaryButtonText || 'Kontakt',
    secondaryButtonUrl: `tel:${siteSettings.phoneRaw || '28907550'}`,
    imageUrl: homePage.introImage || getSection('home_intro').imageUrl || siteSettings.artistImage
  }

  const stylesSection = {
    title: homePage.stylesTitle || getSection('home_styles').title || 'Tatoveringsstilarter',
    subtitle: homePage.stylesSubtitle || getSection('home_styles').subtitle || 'Jeg arbejder med et bredt udvalg af stilarter skræddersyet til dine ønsker.',
    eyebrow: homePage.stylesEyebrow || getSection('home_styles').eyebrow || 'Specialer'
  }

  const gallerySection = {
    title: homePage.galleryTitle || getSection('home_gallery').title || 'Udvalgte Tatoveringer',
    buttonText: homePage.galleryButtonText || getSection('home_gallery').buttonText || 'Se hele galleriet',
    buttonUrl: '/galleri',
    secondaryButtonText: homePage.gallerySecondaryButtonText || getSection('home_gallery').secondaryButtonText || 'Udforsk hele galleriet',
    secondaryButtonUrl: '/galleri'
  }

  const processSection = {
    title: homePage.processTitle || getSection('home_process').title || 'Sådan foregår det',
    subtitle: homePage.processSubtitle || getSection('home_process').subtitle || 'Fra den første idé til din færdige tatovering tager jeg dig trygt igennem alle trin.',
    eyebrow: homePage.processEyebrow || getSection('home_process').eyebrow || 'Processen'
  }

  const currentProcessSteps = (processSteps && processSteps.length > 0)
    ? processSteps
    : [
        {
          step: '01',
          title: 'Idé og Bookingforespørgsel',
          description: 'Du sender mig din idé, dine ønsker til størrelse og placering samt eventuelle referencebilleder via formularen eller direkte pr. telefon.'
        },
        {
          step: '02',
          title: 'Dialog og Rådgivning',
          description: 'Jeg gennemgår din forespørgsel, vurderer stilen og rådgiver dig om den bedste løsning i forhold til kroppens konturer og hudtype.'
        },
        {
          step: '03',
          title: 'Design og Forberedelse',
          description: 'Motivet skræddersyes til dig. På dagen tilpasses stencilen nøjagtigt til din krop, så du kan se og godkende placeringen før jeg starter.'
        },
        {
          step: '04',
          title: 'Tatovering og Efterbehandling',
          description: 'Selve sessionen udføres under sterile, trygge forhold. Bagefter får du grundig instruktion og rådgivning i korrekt efterbehandling.'
        }
      ]

  const contactSection = {
    title: homePage.ctaTitle || getSection('home_contact_banner').title || 'Har du spørgsmål inden du booker?',
    eyebrow: homePage.ctaEyebrow || getSection('home_contact_banner').eyebrow || 'Kontakt',
    subtitle: homePage.ctaSubtitle || getSection('home_contact_banner').subtitle || 'Du er altid velkommen til at ringe direkte til mig for en uforpligtende snak om ideer, priser og ledige tider.',
    buttonText: homePage.ctaButtonText || getSection('home_contact_banner').buttonText || `Ring nu: ${siteSettings.phone}`,
    buttonUrl: homePage.ctaButtonUrl || getSection('home_contact_banner').buttonUrl || `tel:${siteSettings.phoneRaw}`,
    secondaryButtonText: homePage.ctaSecondaryButtonText || getSection('home_contact_banner').secondaryButtonText || siteSettings.email,
    secondaryButtonUrl: homePage.ctaSecondaryButtonUrl || getSection('home_contact_banner').secondaryButtonUrl || `mailto:${siteSettings.email}`
  }

  // Aggregate CMS Gallery images
  const allImages = React.useMemo(() => {
    let list = []
    const seenUrls = new Set()

    // Include images from tattooStyles (CMS)
    if (tattooStyles && tattooStyles.length > 0) {
      tattooStyles.forEach((style) => {
        const styleImgs = Array.isArray(style.images) && style.images.length > 0
          ? style.images
          : (style.image ? [style.image] : [])
        styleImgs.forEach((imgUrl, i) => {
          if (imgUrl && typeof imgUrl === 'string' && !seenUrls.has(imgUrl)) {
            seenUrls.add(imgUrl)
            list.push({
              id: `style-${style.id || style.slug}-${i}`,
              title: style.title || 'Tatovering',
              src: imgUrl,
              alt: `${style.title} tatovering af Docuit ink`,
            })
          }
        })
      })
    }

    if (galleries && galleries.length > 0) {
      galleries.forEach((g) => {
        const imagesArr = g.images || (g.data?.image ? (Array.isArray(g.data.image) ? g.data.image : [g.data.image]) : [])
        const altList = (g.alt_texts || g.data?.alt || '').split(',').map(s => s.trim())
        imagesArr.forEach((imgUrl, i) => {
          if (imgUrl && typeof imgUrl === 'string' && !seenUrls.has(imgUrl)) {
            seenUrls.add(imgUrl)
            list.push({
              id: `${g.id || 'cms'}-${i}`,
              title: g.title || g.data?.title || 'Tatovering',
              src: imgUrl,
              alt: altList[i] || g.title || 'Tatovering af Docuit ink',
            })
          }
        })
      })
    }

    // Merge with default GALLERY_ITEMS if missing or to ensure a complete gallery
    GALLERY_ITEMS.forEach(gi => {
      if (gi.src && !seenUrls.has(gi.src)) {
        seenUrls.add(gi.src)
        list.push(gi)
      }
    })

    return list.length > 0 ? list : GALLERY_ITEMS
  }, [galleries, tattooStyles])

  const featuredImages = isExpanded ? allImages : allImages.slice(0, 6)

  const openLightbox = (index) => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  const heroBgImage = heroSection.imageUrl || siteSettings.heroImage
  const artistImg = introSection.imageUrl || siteSettings.artistImage

  return (
    <div className="min-h-screen bg-[#0f1013] text-[#f4f3f0]">
      <SEO
        title="Docuit ink | Tatovør & Tatoveringsstudie i Vester Sottrup nær Sønderborg"
        description="Leder du efter en professionel tatovør i Vester Sottrup nær Sønderborg? Hos Docuit ink skaber jeg unikke, skræddersyede tatoveringer med fokus på høj hygiejne og kunstnerisk håndværk."
      />

      {/* Hero Section */}
      <section className="relative min-h-[65vh] sm:min-h-[50vh] lg:min-h-[55vh] flex items-center justify-center overflow-hidden border-b border-[#282b34]">
        {/* Background Image with Dark Vignette Overlay */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src={heroBgImage}
            alt="Docuit ink tattoo studio i Vester Sottrup nær Sønderborg"
            className="w-full h-full object-cover object-center scale-[1.85] sm:scale-[2] transform filter brightness-[0.45] contrast-[1.1]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f1013] via-[#0f1013]/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f1013]/90 via-transparent to-[#0f1013]/90" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-14 text-center">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-wider text-white leading-tight mb-4 uppercase">
            {heroSection.title}
          </h1>

          {heroSection.content ? (
            typeof heroSection.content === 'string' && heroSection.content.startsWith('<') ? (
              <div
                className="max-w-2xl mx-auto text-base sm:text-lg text-neutral-300 font-sans leading-relaxed mb-8"
                dangerouslySetInnerHTML={{ __html: heroSection.content }}
              />
            ) : (
              <p className="max-w-2xl mx-auto text-base sm:text-lg text-neutral-300 font-sans leading-relaxed mb-8">
                {heroSection.content}
              </p>
            )
          ) : heroSection.subtitle ? (
            <p className="max-w-2xl mx-auto text-base sm:text-lg text-neutral-300 font-sans leading-relaxed mb-8">
              {heroSection.subtitle}
            </p>
          ) : null}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to={heroSection.buttonUrl || '/booking'}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 bg-[#cbb05e] hover:bg-[#d8be65] text-[#0f1013] font-bold uppercase tracking-wider text-xs rounded-full shadow-lg hover:shadow-[#cbb05e]/20 transition-all cursor-pointer"
            >
              {heroSection.buttonText || 'Send bookingforespørgsel'}
              <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
            </Link>

            <a
              href={heroSection.secondaryButtonUrl || `tel:${siteSettings.phoneRaw}`}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 bg-transparent hover:bg-white/10 text-white font-semibold uppercase tracking-wider text-xs rounded-full border border-white transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-[#cbb05e]" aria-hidden="true" />
              <span className="whitespace-nowrap">{siteSettings.phone}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Kort Introduktion & Om */}
      <section className="py-20 bg-[#f5f4f0] text-[#141519] border-b border-[#e2dec6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-5 relative order-2 lg:order-1">
              <div className="relative rounded-lg overflow-hidden border border-[#dedad0] shadow-xl">
                <img
                  src={artistImg}
                  alt="Tatovør hos Docuit ink i Vester Sottrup nær Sønderborg"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>

            <div className="lg:col-span-7 space-y-6 order-1 lg:order-2">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-[#141519] leading-tight">
                {introSection.title}
              </h2>

              {introSection.content ? (
                typeof introSection.content === 'string' && introSection.content.startsWith('<') ? (
                  <div
                    className="prose text-neutral-700 text-sm sm:text-base leading-relaxed space-y-4"
                    dangerouslySetInnerHTML={{ __html: introSection.content }}
                  />
                ) : (
                  <p className="text-neutral-700 text-sm sm:text-base leading-relaxed">
                    {introSection.content}
                  </p>
                )
              ) : null}

              <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <Link
                  to={introSection.buttonUrl || '/booking'}
                  className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 bg-[#141519] hover:bg-neutral-800 text-white font-semibold uppercase tracking-wider text-xs sm:text-sm rounded-full transition-all cursor-pointer shadow-md"
                >
                  <i className="fa-solid fa-calendar-check text-[#cbb05e]" aria-hidden="true" />
                  <span>{introSection.buttonText || 'Book Tid'}</span>
                </Link>

                <a
                  href={introSection.secondaryButtonUrl || `tel:${siteSettings.phoneRaw}`}
                  className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 bg-transparent hover:bg-[#141519]/10 text-[#141519] font-semibold uppercase tracking-wider text-xs sm:text-sm rounded-full border border-[#141519] transition-colors"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span>{introSection.secondaryButtonText || 'Kontakt'}</span>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Stilarter Oversigt */}
      <section className="py-20 bg-[#0f1013] border-b border-[#282b34]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-serif font-bold text-white mt-2 mb-4">
              {stylesSection.title}
            </h2>
            {stylesSection.subtitle && (
              <p className="text-neutral-400 text-sm sm:text-base">
                {stylesSection.subtitle}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tattooStyles.slice(0, 3).map((style) => (
              <Link
                key={style.id}
                to={`/galleri?stil=${style.id}`}
                className="group relative bg-[#17191e] border border-[#282b34] rounded-lg overflow-hidden transition-all flex flex-col cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={style.image}
                    alt={style.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#17191e] via-transparent to-transparent" />
                </div>
                <div className="p-4 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="font-serif font-bold text-white text-base group-hover:text-white transition-colors">
                      {style.title}
                    </h3>
                    <p className="text-xs text-neutral-400 group-hover:text-neutral-200 mt-1 line-clamp-2 transition-colors">
                      {style.tagline}
                    </p>
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs text-white font-semibold uppercase tracking-wider">
                    {style.buttonText || 'Se arbejde'} <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Udvalgte Tatoveringer (Gallery Preview with Lightbox) */}
      <section className="py-20 bg-white text-[#141519] border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-[#141519]">
                {gallerySection.title}
              </h2>
            </div>

            <Link
              to={gallerySection.buttonUrl || '/galleri'}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#17191e] border border-[#17191e] hover:bg-[#cbb05e] hover:text-[#0f1013] text-white rounded-full text-xs uppercase font-semibold tracking-wider transition-colors cursor-pointer"
            >
              {gallerySection.buttonText || 'Se hele galleriet'}
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </Link>
          </div>

          {/* Grid with Lightbox open trigger */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredImages.map((img, idx) => (
              <div
                key={img.id}
                onClick={() => openLightbox(idx)}
                className="group relative bg-white border border-[#dad5c9] rounded-lg overflow-hidden cursor-pointer hover:border-[#c87a53] transition-all shadow-md"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') openLightbox(idx)
                }}
                aria-label={`Åbn ${img.title} i stor visning`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-neutral-200 relative">
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="w-12 h-12 rounded-full bg-[#cbb05e] text-[#0f1013] flex items-center justify-center shadow-xl transform scale-90 group-hover:scale-100 transition-transform">
                      <i className="fa-solid fa-magnifying-glass text-lg" aria-hidden="true" />
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            {allImages.length > 6 && (
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#17191e] hover:bg-black text-white font-semibold uppercase tracking-wider text-xs rounded-full transition-colors shadow-md cursor-pointer"
              >
                {isExpanded ? (
                  <>
                    Vis færre tatoveringer
                    <i className="fa-solid fa-chevron-up text-xs" aria-hidden="true" />
                  </>
                ) : (
                  <>
                    Vis flere tatoveringer
                    <i className="fa-solid fa-chevron-down text-xs" aria-hidden="true" />
                  </>
                )}
              </button>
            )}

            <Link
              to={gallerySection.secondaryButtonUrl || '/galleri'}
              className="inline-flex items-center gap-3 px-8 py-3.5 bg-[#cbb05e] hover:bg-[#d8be65] text-[#0f1013] font-bold uppercase tracking-wider text-xs rounded-full transition-colors shadow-lg"
            >
              {gallerySection.secondaryButtonText || 'Udforsk hele galleriet'}
            </Link>
          </div>
        </div>
      </section>

      {/* Processen Section */}
      <section className="py-20 bg-[#f4f3ef] text-[#141519] border-b border-[#e2dec6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-[#141519] uppercase">
              {processSection.title}
            </h2>
            {processSection.subtitle && (
              <p className="text-neutral-600 text-sm sm:text-base mt-2">
                {processSection.subtitle}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-8 lg:gap-y-0">
            {currentProcessSteps.map((ps, idx) => {
              const borderClasses = [
                idx % 2 === 0 ? 'sm:border-r sm:border-neutral-400' : 'sm:border-r-0',
                idx < 3 ? 'lg:border-r lg:border-neutral-400' : 'lg:border-r-0',
              ].join(' ');

              return (
                <div
                  key={`process-step-${ps.step || idx}-${idx}`}
                  className={`p-6 lg:px-8 flex flex-col justify-between ${borderClasses}`}
                >
                  <div>
                    <span className="text-[#141519] font-serif font-bold text-base block mb-2 uppercase tracking-wider">
                      {ps.step}. {ps.title}
                    </span>
                    <p className="text-xs text-neutral-600 leading-relaxed">
                      {ps.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Direkte Kontakt Banner */}
      <section className="py-16 bg-white text-[#141519] border-b border-neutral-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-serif font-bold text-[#141519] mb-3">
            {contactSection.title}
          </h3>
          <p className="text-neutral-700 text-sm max-w-lg mx-auto mb-6">
            {contactSection.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5 sm:gap-4">
            <a
              href={`tel:${siteSettings.phoneRaw}`}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 sm:gap-3 px-5 py-2.5 sm:px-8 sm:py-4 bg-[#cbb05e] hover:bg-[#d8be65] text-[#0f1013] font-bold uppercase tracking-wider text-xs sm:text-sm rounded-full transition-colors shadow-md whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-xs sm:text-sm" aria-hidden="true" />
              <span className="whitespace-nowrap">{contactSection.buttonText || `Ring nu: ${siteSettings.phone}`}</span>
            </a>
            <a
              href={`mailto:${siteSettings.email}`}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 sm:gap-3 px-5 py-2.5 sm:px-8 sm:py-4 bg-[#17191e] border border-[#17191e] hover:bg-black text-white font-semibold uppercase tracking-wider text-xs sm:text-sm rounded-full transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-[#cbb05e] text-xs sm:text-sm" aria-hidden="true" />
              <span className="whitespace-nowrap">{contactSection.secondaryButtonText || siteSettings.email}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <LightboxModal
        isOpen={lightboxOpen}
        currentIndex={lightboxIndex}
        images={featuredImages}
        onClose={() => setLightboxOpen(false)}
        onIndexChange={(newIdx) => setLightboxIndex(newIdx)}
      />
    </div>
  )
}
